<template>
    <div class="max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 p-4 border-b">
        <h2 class="text-2xl font-bold text-gray-800">{{ isEditMode ? 'Edit & Resubmit Request' : 'Create Cash Request' }}</h2>
        <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700 transition-colors">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Reversal Reason Banner -->
      <div v-if="isEditMode && existingRequest?.reversal?.reason" class="mx-4 mb-4 bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r">
        <div class="flex items-center mb-1">
          <div class="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
          <span class="text-sm font-semibold text-amber-800">Sent back for changes</span>
        </div>
        <p class="text-sm text-amber-900 mb-1">{{ existingRequest.reversal.reason }}</p>
        <p class="text-xs text-amber-700" v-if="existingRequest.reversal.reversedBy">
          By {{ existingRequest.reversal.reversedBy.name }}<span v-if="existingRequest.reversal.reversedByRole"> ({{ existingRequest.reversal.reversedByRole }})</span>
        </p>
        <p class="text-xs text-amber-700 mt-1">Make your corrections below - this will go straight back to them for review.</p>
      </div>

      <form @submit.prevent="submitForm" class="space-y-6 pb-6">
        <!-- Basic Information -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">Basic Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Amount (RWF)</label>
              <input
                v-model="formData.amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter amount"
                class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                v-model="formData.priority"
                class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              >
                <option value="">Select Priority</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Purpose</label>
            <textarea
              v-model="formData.purpose"
              placeholder="Describe the purpose of this request"
              rows="3"
              class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
              required
            ></textarea>
          </div>
        </div>

        <!-- Beneficiary & Payment Details -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">Beneficiary & Payment Details</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Beneficiary</label>
              <input
                v-model="formData.supplier"
                type="text"
                placeholder="Beneficiary name"
                class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Beneficiary Account/MoMo</label>
              <input
                v-model="formData.beneficiaryAccount"
                type="text"
                placeholder="Account number or MoMo number"
                class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <select
                v-model="formData.paymentMethod"
                class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              >
                <option value="">Select Payment Method</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Mobile Money">Mobile Money</option>
                <option value="Cash">Cash</option>
              </select>
            </div>

          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Preferred Payment Date</label>
            <input
              v-model="formData.preferredPaymentDate"
              type="date"
              :min="today"
              class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              required
            />
          </div>
        </div>
        </div>

        <!-- Document Attachments -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">
            Document Attachments <span class="font-normal text-sm text-gray-500">({{ isEditMode ? 'leave blank to keep the current file' : 'Optional' }})</span>
          </h3>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Invoice/Quotation
                <span v-if="isEditMode && existingRequest?.attachments?.invoice" class="text-xs text-green-700 font-normal">- current file attached</span>
              </label>
              <input
                ref="invoiceFile"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Contract/Agreement
                <span v-if="isEditMode && existingRequest?.attachments?.contract" class="text-xs text-green-700 font-normal">- current file attached</span>
              </label>
              <input
                ref="contractFile"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Other Supporting Documents
                <span v-if="isEditMode && existingRequest?.attachments?.otherDocuments?.length" class="text-xs text-green-700 font-normal">- {{ existingRequest.attachments.otherDocuments.length }} current file(s) attached</span>
              </label>
              <input
                ref="otherFiles"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p class="text-xs text-gray-500 mt-1">You can select multiple files{{ isEditMode ? ' - this will replace the current set' : '' }}</p>
            </div>
          </div>
        </div>

        <!-- Submit Button - Sticky at bottom -->
        <div class="sticky bottom-0 bg-white p-4 border-t">
          <div class="flex justify-between space-x-4">
            <button
              type="button"
              @click="$emit('close')"
              class="px-6 py-3 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-6 py-3 rounded-lg text-white transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              :class="isEditMode ? 'bg-amber-600 hover:bg-amber-700 focus:ring-2 focus:ring-amber-300' : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-300'"
              :disabled="!isFormValid || isSubmitting"
            >
              {{ isSubmitting ? (isEditMode ? 'Resubmitting...' : 'Submitting...') : (isEditMode ? 'Resubmit Request' : 'Submit Request') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </template>

  <script>
  import axios from 'axios';

  export default {
    props: {
      mode: {
        type: String,
        default: 'create' // 'create' | 'edit'
      },
      existingRequest: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        isSubmitting: false,
        formData: (this.mode === 'edit' && this.existingRequest) ? this.buildFormDataFromExisting() : {
          amount: '',
          purpose: '',
          supplier: '',
          beneficiaryAccount: '',
          paymentMethod: '',
          preferredPaymentDate: '',
          priority: ''
        }
      };
    },
    computed: {
      isEditMode() {
        return this.mode === 'edit' && !!this.existingRequest;
      },
      isFormValid() {
        return this.formData.amount &&
               this.formData.purpose &&
               this.formData.paymentMethod &&
               this.formData.preferredPaymentDate &&
               this.formData.priority;
      },
      today() {
        return new Date().toISOString().split('T')[0];
      }
    },
    methods: {
      buildFormDataFromExisting() {
        const r = this.existingRequest || {};
        return {
          amount: r.amount ?? '',
          purpose: r.purpose ?? '',
          supplier: r.supplier && r.supplier !== 'N/A' ? r.supplier : '',
          beneficiaryAccount: r.beneficiaryAccount ?? '',
          paymentMethod: r.paymentMethod ?? '',
          preferredPaymentDate: r.preferredPaymentDate ? String(r.preferredPaymentDate).split('T')[0] : '',
          priority: r.priority ?? ''
        };
      },

      async submitForm() {
        if (!this.isFormValid) {
          alert('Please fill in all required fields');
          return;
        }

        if (this.isEditMode) {
          await this.resubmitRequest();
        } else {
          await this.createRequest();
        }
      },

      async createRequest() {
        this.isSubmitting = true;

        try {
          // Create FormData for file upload
          const formData = new FormData();

          // Add text fields
          Object.keys(this.formData).forEach(key => {
            formData.append(key, this.formData[key]);
          });

          // Add files if they exist
          if (this.$refs.invoiceFile.files[0]) {
            formData.append('invoice', this.$refs.invoiceFile.files[0]);
          }

          if (this.$refs.contractFile.files[0]) {
            formData.append('contract', this.$refs.contractFile.files[0]);
          }

          if (this.$refs.otherFiles.files.length > 0) {
            Array.from(this.$refs.otherFiles.files).forEach(file => {
              formData.append('otherDocuments', file);
            });
          }

          await axios.post('https://guarantee.iwacufinance.com/cash-request/api/requests', formData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            },
          });

          // Reset form
          this.resetForm();
          this.$emit('request-created');

        } catch (error) {
          console.error('Error creating request:', error);
          alert('Failed to create request: ' + (error.response?.data?.message || error.message));
        } finally {
          this.isSubmitting = false;
        }
      },

      async resubmitRequest() {
        this.isSubmitting = true;

        try {
          const formData = new FormData();

          Object.keys(this.formData).forEach(key => {
            formData.append(key, this.formData[key]);
          });

          if (this.$refs.invoiceFile.files[0]) {
            formData.append('invoice', this.$refs.invoiceFile.files[0]);
          }

          if (this.$refs.contractFile.files[0]) {
            formData.append('contract', this.$refs.contractFile.files[0]);
          }

          if (this.$refs.otherFiles.files.length > 0) {
            Array.from(this.$refs.otherFiles.files).forEach(file => {
              formData.append('otherDocuments', file);
            });
          }

          await axios.post(
            `https://guarantee.iwacufinance.com/cash-request/api/requests/${this.existingRequest._id}/resubmit`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data'
              },
            }
          );

          this.$emit('request-resubmitted');

        } catch (error) {
          console.error('Error resubmitting request:', error);
          alert('Failed to resubmit request: ' + (error.response?.data?.message || error.message));
        } finally {
          this.isSubmitting = false;
        }
      },

      resetForm() {
        this.formData = {
          amount: '',
          purpose: '',
          supplier: '',
          beneficiaryAccount: '',
          paymentMethod: '',
          preferredPaymentDate: '',
          priority: ''
        };

        // Reset file inputs
        this.$refs.invoiceFile.value = '';
        this.$refs.contractFile.value = '';
        this.$refs.otherFiles.value = '';
      }
    }
  };
  </script>
